import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import Cookies from "js-cookie";

import {
  httpClient,
  getLoginUserAPI,
  getPartnerAnnouncementsAPI,
} from "@/util/api/API";
import { setAuthentication } from "@/util/api/setAuthentication";
import { UserInfo, CookieKey } from "@/models/auth";
import { PartnerAnnouncement } from "@/models/common";
import { setGAUser } from "@/util/analytics";
import { recordUrlTrackingAccessAPI } from "@/util/api/urlTracking";
// import { EDIT } from "@/mock/auth";

export type AuthUserContextType = {
  user: UserInfo | null;
  location: string;
  isLoading: boolean;
  triggerHock: (to: string, from: string) => void;
  infoHeaders: PartnerAnnouncement[];
};

export const AuthUserContext = createContext<AuthUserContextType>({
  user: null,
  location: "",
  isLoading: true,
  triggerHock: () => { },
  infoHeaders: [],  // ← 空配列を明示
});


type Props = {
  children: ReactNode;
};

type CookieCheck = {
  token: string;
};

export const AuthUserProvider = (props: Props) => {
  const { children } = props;
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [location, setLocation] = useState("");
  const [infoHeaders, setInfoHeaders] = useState<PartnerAnnouncement[]>([]);

  // パス変更時に認証api実行トリガー
  const triggerHock = (to: string, from: string) => {
    if (to !== from) {
      setLoading(true);
      setLocation(to);
    }
  };

  /**
   * 画面権限取得
   */
  const getScreenPermission = async () => {
    try {
      // 画面権限とユーザーIDを取得
      const res = await getLoginUserAPI();
      if (res) {
        const user = {
          id: res.data.id,
          name: res.data.name,
          role: res.data.role,
          auth: res.data.auth,
        };
        return user;
      } else {
        return null;
      }
      // return {
      //   name: "name",
      // role: 3,
      //   auth: EDIT,
      // };
    } catch (err) {
      return null;
    }
  };

  // 送信済みのtr_tokenを保持し、同一トークンでの再送信を防ぐ
  const recordedTrTokensRef = useRef<Set<string>>(new Set());

  // tr_token検知→ベストエフォートでURLアクセスを記録する（失敗しても画面には影響させない）
  const recordUrlTrackingAccessIfNeeded = useCallback((trToken: string) => {
    if (recordedTrTokensRef.current.has(trToken)) return;
    recordedTrTokensRef.current.add(trToken);
    recordUrlTrackingAccessAPI(trToken).catch(() => {
      // ベストエフォート処理のため、失敗時も何もしない
    });
  }, []);

  // アナウンス情報を取得
  const getPartnerAnnouncement = useCallback(() => {
    getPartnerAnnouncementsAPI()
      .then((res) => {
        // バックエンドに到達できていない場合（開発サーバーが index.html を返す等）は
        // 配列以外が返ってくることがあるため、後続の.mapで画面全体を壊さないよう防御する
        setInfoHeaders(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        // 何もしない
      });
  }, [setInfoHeaders]);

  // トークンを取得
  const cookieCheck = (): CookieCheck | undefined => {
    // gxタクモニユーザー
    let gxoperator: string | undefined = Cookies.get(CookieKey.gxoperator);
    if (gxoperator) {
      return { token: gxoperator };
    }

    return undefined;
  };

  // create basic authentication string first load
  useEffect(() => {
    setAuthentication();
  }, []);

  // cookieのトークンをヘッダーに設定、ユーザー情報を取得
  useEffect(() => {
    if (!location) return;
    const trToken = new URLSearchParams(window.location.search).get(
      "tr_token"
    );
    const cookie: CookieCheck | undefined = cookieCheck();
    if (cookie?.token) {
      httpClient.defaults.headers.common["X-MoT-Access-Token"] = cookie.token;
      getScreenPermission().then((user) => {
        if (user) {
          setUser({ ...user });
          setLoading(false);
          getPartnerAnnouncement();
          setGAUser(user);
          if (trToken) {
            recordUrlTrackingAccessIfNeeded(trToken);
          }
        } else {
          setUser(null);
          setLoading(false);
          setGAUser(undefined);
        }
      });
    } else {
      setUser(null);
      setLoading(false);
      setGAUser(undefined);
    }
  }, [location, getPartnerAnnouncement, recordUrlTrackingAccessIfNeeded]);

  const value: AuthUserContextType = {
    user,
    location,
    isLoading,
    triggerHock,
    infoHeaders,
  };
  return (
    <AuthUserContext.Provider value={value}>
      {children}
    </AuthUserContext.Provider>
  );
};
